<template lang="pug">
.create-ove-view
  .header-ove
    .title-section
      b-icon.back-arrow(icon="arrow-left-square-fill" @click="$router.push('/oportunidad-venta-equipo')")
      h4.title {{ isEdit ? 'Editar Cotización de Equipo' : 'Nueva Cotización de Equipo' }}
  
  .breadcrumbs-ove
    span.text-muted Inicio / Oportunidad de Venta de Equipos / 
    span.text-dark {{ isEdit ? 'Editar Cotización' : 'Nueva Cotización' }}

  .accordion-wrapper
    // Paso 1: Datos del Cliente
    .step-card(:class="{ active: currentStep === 1 }")
      .step-header(@click="toggleStep(1)")
        .step-number 1
        .step-title Datos del Cliente
        b-icon(:icon="currentStep === 1 ? 'chevron-up' : 'chevron-down'")
      
      b-collapse(v-model="isStep1Open")
        .step-body
          Step1Cliente(:form="form.cliente" :$v="$v.form.cliente")
          .step-actions.text-right.mt-4
            b-button(variant="primary" @click="nextStep(1)") Siguiente Paso

    // Paso 2: Equipo y Cotización
    .step-card.mt-3(:class="{ active: currentStep === 2 }")
      .step-header(@click="toggleStep(2)")
        .step-number 2
        .step-title Cotización de Equipos
        b-icon(:icon="currentStep === 2 ? 'chevron-up' : 'chevron-down'")
      
      b-collapse(v-model="isStep2Open")
        .step-body
          Step2Equipo(
            :form="form.cotizacion" 
            :allWarranties="allWarranties"
            :$v="$v.form.cotizacion" 
            @update-totals="calculateTotals"
          )
          .step-actions.text-right.mt-4
            b-button.ml-2(variant="primary" @click="nextStep(2)") Calcular Totales

    // Paso 3: Resumen y Despacho
    .step-card.mt-3(:class="{ active: currentStep === 3 }")
      .step-header(@click="toggleStep(3)")
        .step-number 3
        .step-title Resumen y Despacho
        b-icon(:icon="currentStep === 3 ? 'chevron-up' : 'chevron-down'")
      
      b-collapse(v-model="isStep3Open")
        .step-body
          Step3Resumen(
            :form="form.despacho" 
            :totals="totals" 
            :loading="isSubmitting"
            @update-totals="calculateTotals"
            @confirm="submitOVE"
          )
          .step-actions.text-right.mt-4
            // Final step action is handled inside Step3Resumen (Confirmar)
</template>

<script>
import { validationMixin } from 'vuelidate'
import {
  required,
  requiredIf,
  email,
  minLength,
} from 'vuelidate/lib/validators'
import { mapActions } from 'vuex'
import Step1Cliente from '@/components/OVE/Step1Cliente.vue'
import Step2Equipo from '@/components/OVE/Step2Equipo.vue'
import Step3Resumen from '@/components/OVE/Step3Resumen.vue'

export default {
  name: 'CreateOVEView',
  mixins: [validationMixin],
  components: {
    Step1Cliente,
    Step2Equipo,
    Step3Resumen,
  },
  props: {
    ove_id: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      currentStep: 1,
      isSubmitting: false,
      loading: false,
      form: {
        cliente: {
          id: null,
          cliente_tipo: 'persona',
          rut: '',
          nombre: '',
          apellido: '',
          email: '',
          telefono: '',
          region: null,
          comuna: null,
          direccion: '',
          contacto_nombre: '',
          contacto_telefono: '',
        },
        cotizacion: {
          items: [],
          garantia_tipo: null,
          comentario: '',
        },
        despacho: {
          despacho_tipo: 'retiro',
          direccion_envio: '',
          costo_envio: 0,
        },
      },
      totals: {
        subtotal: 0,
        iva: 0,
        descuento: 0,
        envio: 0,
        total: 0,
      },
      allWarranties: [],
    }
  },
  validations: {
    form: {
      cliente: {
        rut: { required },
        nombre: { required },
        email: { required, email },
        // Una empresa no siempre tiene teléfono registrado (el modelo
        // Empresa del backend no tiene esa columna) — solo se exige para
        // Persona, donde sí hay una fuente real del dato.
        telefono: {
          required: requiredIf((cliente) => cliente.cliente_tipo === 'persona'),
        },
        region: { required },
        comuna: { required },
        direccion: { required },
      },
      cotizacion: {
        items: {
          required,
          minLength: minLength(1),
        },
      },
    },
  },
  computed: {
    isStep1Open() {
      return this.currentStep === 1
    },
    isStep2Open() {
      return this.currentStep === 2
    },
    isStep3Open() {
      return this.currentStep === 3
    },
    isEdit() {
      return !!this.ove_id
    },
  },
  async mounted() {
    await this.fetchWarranties()
    if (this.isEdit) {
      await this.fetchOveDetail()
    }
  },
  methods: {
    ...mapActions('OVEStore', [
      'createOve',
      'editarOve',
      'getOveDetail',
      'listGarantiasDisponibles',
    ]),

    async fetchWarranties() {
      try {
        this.allWarranties = await this.listGarantiasDisponibles()
      } catch (error) {
        console.error('Error cargando garantías:', error)
      }
    },

    async fetchOveDetail() {
      this.loading = true
      try {
        const ove = await this.getOveDetail(this.ove_id)

        // Poblar formulario Paso 1
        this.form.cliente = {
          id: ove.cliente,
          rut: ove.rut,
          nombre: ove.nombre,
          apellido: ove.apellido,
          email: ove.correo_electronico,
          telefono: ove.telefono,
          region: ove.region,
          comuna: ove.comuna,
          direccion: ove.direccion,
          contacto_nombre: ove.nombre_cliente,
        }

        // Poblar formulario Paso 2
        this.form.cotizacion.items = ove.detalles.map((d) => ({
          id: d.equipo,
          modelo: d.equipo_nombre,
          precio: d.precio_unitario,
          cantidad: d.cantidad,
          descuento: d.porcentaje_descuento,
          tipo_garantia: d.garantia_extendida || 'normal',
          precio_garantia: d.precio_garantia,
          imagen: d.imagen_url,
          serie: d.equipo_numero_serie,
          permite_garantia: d.permite_garantia,
        }))
        this.form.cotizacion.comentario = ove.comentario_vendedor

        // Poblar formulario Paso 3
        this.form.despacho.despacho_tipo = ove.tipo_despacho
        this.form.despacho.direccion_envio = ove.direccion
        this.form.despacho.costo_envio = ove.costo_envio || 0

        this.calculateTotals()
      } catch (error) {
        console.error('Error fetching OVE for edit:', error)
        this.$bvToast.toast('Error al cargar OVE para editar', {
          variant: 'danger',
        })
      } finally {
        this.loading = false
      }
    },

    toggleStep(step) {
      if (step < this.currentStep || this.validateStep(step - 1)) {
        this.currentStep = step
      }
    },

    nextStep(step) {
      if (this.validateStep(step)) {
        this.currentStep = step + 1
      }
    },

    validateStep(step) {
      if (step === 1) {
        this.$v.form.cliente.$touch()
        return !this.$v.form.cliente.$error
      }
      if (step === 2) {
        this.$v.form.cotizacion.$touch()
        if (this.$v.form.cotizacion.$error) {
          this.$bvToast.toast('Debe seleccionar al menos un equipo', {
            variant: 'warning',
          })
          return false
        }
        return true
      }
      return true
    },

    calculateTotals() {
      let totalBrutoItems = 0
      let totalDescuentoBruto = 0

      this.form.cotizacion.items.forEach((item) => {
        const itemSub = item.precio * item.cantidad
        // Cada unidad se guarda como un DetalleVentaEquipo independiente.
        const itemDcto =
          Math.floor((item.precio * item.descuento) / 100) * item.cantidad

        // Precio garantía dinámico
        let precioGarantia = 0
        if (item.tipo_garantia !== 'normal') {
          const g = this.allWarranties.find((w) => w.id === item.tipo_garantia)
          if (g) precioGarantia = g.precio
          else precioGarantia = item.precio_garantia || 0 // Usar el que ya tiene si no lo encuentra (edición)
        }
        item.precio_garantia = precioGarantia

        totalBrutoItems += itemSub + precioGarantia * item.cantidad
        totalDescuentoBruto += itemDcto
      })

      // Envío
      let costoEnvio = 0
      if (this.form.despacho.despacho_tipo === 'envio') {
        costoEnvio = this.form.despacho.costo_envio || 0
      }

      // Mismo desglose que OportunidadVentaEquipo.resumen_totales() en el backend:
      // Sub Total (neto) − Descuento (neto) + IVA + Envío (IVA incl.) = Total
      const brutoConDescuento = totalBrutoItems - totalDescuentoBruto
      const netoConDescuento = Math.floor((brutoConDescuento * 100) / 119)
      const descuentoNeto = Math.floor((totalDescuentoBruto * 100 + 59) / 119)

      this.totals.subtotal = netoConDescuento + descuentoNeto
      this.totals.descuento = descuentoNeto
      this.totals.iva = brutoConDescuento - netoConDescuento
      this.totals.envio = costoEnvio
      this.totals.total = brutoConDescuento + costoEnvio
    },

    async submitOVE() {
      this.isSubmitting = true
      try {
        const payload = {
          // Datos cliente
          cliente_id: this.form.cliente.id || null,
          nombre: this.form.cliente.nombre,
          apellido: this.form.cliente.apellido,
          rut: this.form.cliente.rut,
          correo_electronico: this.form.cliente.email,
          telefono: this.form.cliente.telefono,
          region: this.form.cliente.region,
          comuna: this.form.cliente.comuna,
          direccion: this.form.cliente.direccion,

          // Despacho
          tipo_despacho: this.form.despacho.despacho_tipo,
          costo_envio: this.totals.envio,

          // Detalles: resolver equipoIds según selección manual
          detalles: this.form.cotizacion.items.flatMap((item) => {
            if (!item.seleccionesEquipo) {
              const ids = item.equipoIds
                ? item.equipoIds
                    .slice(0, item.cantidad)
                    .map((equipo) =>
                      typeof equipo === 'object' ? equipo.id : equipo
                    )
                : [item.id]
              return ids.map((equipo_id) => ({
                equipo_id,
                precio_unitario: item.precio,
                porcentaje_descuento: item.descuento,
                garantia_extendida_id:
                  item.tipo_garantia !== 'normal' ? item.tipo_garantia : null,
                precio_garantia: item.precio_garantia || 0,
              }))
            }

            const elegidosExplicitamente = new Set(
              item.seleccionesEquipo
                .filter((s) => s.equipoId !== null)
                .map((s) => s.equipoId)
            )

            const resueltos = item.seleccionesEquipo.map((sel) => {
              if (sel.equipoId !== null) return sel.equipoId
              // "Cualquiera": tomar el primer equipo no elegido explícitamente
              const fallback = item.equipoIds.find(
                (eq) => !elegidosExplicitamente.has(eq.id)
              )
              if (fallback) {
                // Lo agregamos a elegidos para que el siguiente "Cualquiera" tome el próximo
                elegidosExplicitamente.add(fallback.id)
                return fallback.id
              }
              return item.equipoIds[0].id // Safety net
            })

            return resueltos.map((equipo_id) => ({
              equipo_id,
              precio_unitario: item.precio,
              porcentaje_descuento: item.descuento,
              garantia_extendida_id:
                item.tipo_garantia !== 'normal' ? item.tipo_garantia : null,
              precio_garantia: item.precio_garantia || 0,
            }))
          }),

          // Comentario
          comentario_vendedor: this.form.cotizacion.comentario || '',
        }

        let res
        if (this.isEdit) {
          res = await this.editarOve({ id: this.ove_id, data: payload })
          this.$bvToast.toast('OVE actualizada exitosamente', {
            variant: 'success',
          })
        } else {
          res = await this.createOve(payload)
          this.$bvToast.toast('OVE creada exitosamente', { variant: 'success' })
        }

        const finalId = this.isEdit ? this.ove_id : res.data.id
        this.$router.push(`/oportunidad-venta-equipo/${finalId}`)
      } catch (error) {
        console.error('Error submitting OVE:', error)
        const detalle = this.extraerMensajeError(error)
        this.$bvToast.toast(detalle || 'Error al procesar OVE', {
          variant: 'danger',
          autoHideDelay: 6000,
        })
      } finally {
        this.isSubmitting = false
      }
    },

    // Extrae un mensaje legible de los distintos formatos de error que puede
    // devolver el backend de OVE: serializer.errors de DRF ({campo: [msg]}),
    // {error: 'msg'} (ej. cliente no encontrado) o {errores: ['msg', ...]}
    // (ej. equipo ya vendido).
    extraerMensajeError(error) {
      const data = error.response && error.response.data
      if (!data) return null
      if (typeof data === 'string') return data
      if (data.error) return data.error
      if (Array.isArray(data.errores)) return data.errores.join(' | ')

      const mensajes = Object.entries(data)
        .filter(
          ([, valor]) => Array.isArray(valor) || typeof valor === 'string'
        )
        .map(
          ([campo, valor]) =>
            `${campo}: ${Array.isArray(valor) ? valor.join(', ') : valor}`
        )
      return mensajes.length ? mensajes.join(' | ') : null
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/style/colors.scss';

.create-ove-view {
  padding: 20px;
  background-color: #f5f6fa;
  min-height: 100vh;
}

.header-ove {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;

  .title-section {
    display: flex;
    align-items: center;
    gap: 15px;

    .back-arrow {
      color: $samtek-yellow;
      font-size: 32px;
      cursor: pointer;
    }

    .title {
      margin: 0;
      font-weight: bold;
      color: $samtek-black;
    }
  }
}

.breadcrumbs-ove {
  margin-bottom: 25px;
  font-size: 14px;
}

.accordion-wrapper {
  max-width: 1200px;
  margin: 0 auto;

  .step-card {
    background: white;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
    border: 1px solid transparent;
    transition: all 0.3s ease;

    &.active {
      border-color: $samtek-yellow;
      box-shadow: 0 4px 12px rgba($samtek-yellow, 0.1);
    }

    .step-header {
      padding: 20px;
      display: flex;
      align-items: center;
      cursor: pointer;
      user-select: none;

      .step-number {
        width: 32px;
        height: 32px;
        background: #f5f6fa;
        color: $samtek-grey;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: bold;
        margin-right: 15px;
      }

      .step-title {
        flex: 1;
        font-weight: bold;
        color: $samtek-black;
        font-size: 18px;
      }
    }

    &.active .step-header .step-number {
      background: $samtek-yellow;
      color: $samtek-black;
    }

    .step-body {
      padding: 0 20px 20px 67px;
    }
  }
}
</style>
